/* eslint-disable no-magic-numbers */
import { useState, ReactNode } from 'react';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DialogProps } from '@mui/material/Dialog';
import { Close as CloseIcon } from '@mui/icons-material';
import CSS from 'csstype';

import Loader from '~/components/Loader';
import { useMedia } from "~/hooks/useMedia";
import { useTrans, useNonHookTrans } from "~/hooks/useTrans";

import "./style.less";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
	'& .MuiDialogContent-root': {
		padding: theme.spacing(2),
	},
	'& .MuiDialogActions-root': {
		padding: theme.spacing(1),
	},
}));

interface DialogTitleProps {
	id: string;
	children?: ReactNode;
	onClose: () => void;
}

const BootstrapDialogTitle = (props: DialogTitleProps) => {
	const { children, onClose, ...other } = props;

	return <DialogTitle sx={{ m: 0, p: 2, fontWeight: "bold" }} {...other}>
		{children}
		{onClose
			? <IconButton
				aria-label="close"
				onClick={onClose}
				sx={{
					position: 'absolute',
					right: 8,
					top: 8,
					color: "#008000",
				}}
			>
				<CloseIcon />
			</IconButton>
			: null
		}
	</DialogTitle>;
};

function getMediaQuery(minWidth: string, padding: number): string {
	let newValue = minWidth;
	const match = minWidth.match(/(\d+)px/u);

	if (match && match.length > 0) {
		const pxs = parseFloat(match[1]);
		const pxsWithPadding = pxs + (4 * padding);

		newValue = `${pxsWithPadding}px`;
	}

	return `(min-width: ${newValue})`;
}

interface IDialog extends Omit<DialogProps, "open"> {
	title?: string;
	className?: string;
	contentClassName?: string;
	contentSx?: CSS.Properties;
	children?: ReactNode;
	dialogLoading?: boolean;
	disableSave?: boolean;
	confirmClose?: boolean;
	onlyClose?: boolean;
	errorMsg?: string;
	onClose?: () => void;
	onSave?: () => void;
	cancelCaption?: string;
	saveCaption?: string;
	props?: DialogProps;
	minWidth?: string;
	padding?: number;
	errorLoadPosition?: "bottom" | "top";
}

export default function MyDialog({
	title = "",
	className = "",
	contentClassName = "",
	contentSx = {},
	children = null,
	dialogLoading = false,
	disableSave = false,
	confirmClose = false,
	onlyClose = false,
	errorMsg = "",
	onClose = () => {},
	onSave = () => {},
	cancelCaption = useNonHookTrans().trans("cancelChanges"),
	saveCaption = useNonHookTrans().trans("saveChanges"),
	minWidth = `400px`,
	padding = 32,
	errorLoadPosition = "bottom",
	...props
}: IDialog) {
	const { trans } = useTrans();
	const mediaQueryTest = useMedia(getMediaQuery(minWidth, padding));
	const [lock, setLock] = useState(false);

	function handleClose() {
		if (lock) {
			return;
		}

		if (confirmClose && !lock) {
			// zobrazime info
			setLock(true);

			return;
		}

		onClose();
	}

	function closeDialog() {
		if (!dialogLoading) {
			handleClose();
		}
	}

	return <BootstrapDialog
		onClose={closeDialog}
		open={true}
		className={className}
		{ ...props }
	>
		<BootstrapDialogTitle id="save-dialog-title" onClose={lock ? null : closeDialog}>
			{ !lock && title}
			{ lock && <div className="myDialog__changesAlert">
				{ trans("dialog.hasChanges") }
				<div className="myDialog__changesAlertBtns">
					<Button variant="contained" color="error" onClick={onClose}>
						{ trans("close") }
					</Button>
					<Button variant="outlined" onClick={() => setLock(false)}>
						{ trans("cancel") }
					</Button>
				</div>
			</div> }
		</BootstrapDialogTitle>
		<DialogContent dividers className={contentClassName} style={{ padding: `${padding}px`, minWidth: mediaQueryTest ? minWidth : "auto", boxSizing: "border-box", ...contentSx }}>
			{ errorLoadPosition === "bottom" && children }
			<p className={`myDialog__info ${errorLoadPosition}`}>{ errorMsg }</p>
			{ dialogLoading && <Loader msg={trans("loadingData")} /> }
			{ errorLoadPosition === "top" && children }
		</DialogContent>
		<DialogActions style={{ justifyContent: onlyClose ? "flex-end" : "space-between", padding: "16px 32px" }}>
			<Button autoFocus onClick={closeDialog} variant="outlined" color="success" className="myDialog__button" disabled={dialogLoading || lock}>
				{ cancelCaption }
			</Button>
			{ !onlyClose && <Button onClick={onSave} variant="contained" color="success" className="myDialog__button" disabled={dialogLoading || disableSave || lock}>
				{ saveCaption }
			</Button> }
		</DialogActions>
	</BootstrapDialog>;
}
